/**
 * Builds the contents of the rendering measurement panel.
 *
 * HTML rather than JSX on purpose: this measures how slowly the page draws, and
 * a React subtree re-rendering every second would put its own cost into the
 * numbers it reports. The owning component writes this string into a ref'd node
 * imperatively, so React renders the panel once and never again while it is up.
 */
import { getRenderStats } from './renderMonitor';
const OK = '#7ee08a';
const WARN = '#ffd166';
const BAD = '#ff6b6b';
const DIM = '#8b93a7';
/**
 * Colour for a value where bigger is worse.
 *
 * @param {number} v - The value.
 * @param {number} warn - Threshold for amber.
 * @param {number} bad - Threshold for red.
 * @returns {string}
 */
function _sev(v, warn, bad) {
    if (v >= bad) {
        return BAD;
    }
    if (v >= warn) {
        return WARN;
    }
    return OK;
}
/**
 * Colour for a value where smaller is worse (frame rate).
 *
 * @param {number} v - The value.
 * @param {number} warn - Threshold for amber.
 * @param {number} bad - Threshold for red.
 * @returns {string}
 */
function _sevLow(v, warn, bad) {
    if (v <= bad) {
        return BAD;
    }
    if (v <= warn) {
        return WARN;
    }
    return OK;
}
/**
 * One label/value line.
 *
 * @param {string} label - Left side.
 * @param {string} value - Right side.
 * @param {string} colour - Value colour.
 * @returns {string}
 */
function _row(label, value, colour) {
    return '<div style="display:flex;justify-content:space-between;gap:10px">'
        + `<span style="color:${DIM}">${label}</span>`
        + `<span style="color:${colour ?? '#e6e9ef'};font-variant-numeric:tabular-nums">${value}</span>`
        + '</div>';
}
/**
 * Section heading.
 *
 * @param {string} title - The heading.
 * @returns {string}
 */
function _head(title) {
    return `<div style="margin:8px 0 3px;color:#aab2c5;font-weight:600;`
        + `border-bottom:1px solid rgba(255,255,255,.08);padding-bottom:2px">${title}</div>`;
}
/**
 * Composes the panel body.
 *
 * @returns {string}
 */
function _render() {
    const s = getRenderStats();
    const parts = [];
    parts.push('<div style="font-weight:600;margin-bottom:2px">描画計測</div>');
    // ── Frames. The headline number: what the user actually experiences as
    //    "slow". Below ~20 fps the page feels broken rather than sluggish.
    parts.push(_head('フレーム'));
    parts.push(_row('FPS', s.fps.toFixed(1), _sevLow(s.fps, 30, 20)));
    parts.push(_row('1フレーム', `平均 ${s.frameAvgMs.toFixed(1)} / 最大 ${s.frameMaxMs.toFixed(0)} ms`, _sev(s.frameMaxMs, 100, 300)));
    parts.push(_row('カクつき', `${s.jankFrames} 回 / 5秒`, _sev(s.jankFrames, 10, 40)));
    // ── Long tasks. Separates "JavaScript is hogging the thread" from
    //    "the compositor/GPU cannot keep up" — the two have different fixes.
    parts.push(_head('メインスレッド'));
    if (s.longTaskSupported) {
        parts.push(_row('長いタスク', `${s.longTasks} 回 / 5秒`, _sev(s.longTasks, 5, 15)));
        parts.push(_row('占有', `${(s.longTaskMs / 50).toFixed(1)} %`, _sev(s.longTaskMs / 50, 20, 40)));
        parts.push(_row('最長', `${s.longTaskMaxMs.toFixed(0)} ms`, _sev(s.longTaskMaxMs, 200, 500)));
    }
    else {
        parts.push(_row('長いタスク', '非対応', DIM));
    }
    // ── Input latency. What "the UI does not respond" means numerically.
    parts.push(_head('操作の反応'));
    if (s.eventTimingSupported) {
        if (s.inputCount) {
            parts.push(_row('遅延', `平均 ${s.inputAvgMs.toFixed(0)} / 最大 ${s.inputMaxMs.toFixed(0)} ms`, _sev(s.inputMaxMs, 200, 500)));
            parts.push(_row('操作数', `${s.inputCount} 件 / 5秒`, DIM));
        }
        else {
            parts.push(_row('遅延', '操作待ち', DIM));
        }
    }
    else {
        parts.push(_row('遅延', '非対応', DIM));
    }
    // ── Size of the thing being drawn. A tile count or DOM size far above a
    //    healthy machine's is the cheapest explanation to check first.
    parts.push(_head('ページ規模'));
    parts.push(_row('DOM要素', `${s.domNodes.toLocaleString()} 個`, _sev(s.domNodes, 3000, 8000)));
    parts.push(_row('映像タイル', `${s.videos} 個`, _sev(s.videos, 10, 20)));
    if (s.heapMB !== null) {
        const pct = s.heapLimitMB ? (s.heapMB / s.heapLimitMB) * 100 : 0;
        parts.push(_row('JSメモリ', `${s.heapMB.toFixed(0)} MB (${pct.toFixed(0)}%)`, _sev(pct, 60, 85)));
    }
    else {
        parts.push(_row('JSメモリ', '非対応', DIM));
    }
    // ── A one-line reading, so the panel says what it means rather than
    //    leaving the user to correlate six numbers on a machine that is
    //    already painful to use.
    let verdict = '';
    if (s.fps >= 45) {
        verdict = '描画は正常です';
    }
    else if (s.longTaskSupported && s.longTaskMs / 50 >= 20) {
        verdict = 'JavaScript がメインスレッドを占有しています';
    }
    else if (s.videos >= 10 || s.domNodes >= 8000) {
        verdict = 'ページが大きすぎます（タイル数 / DOM 数）';
    }
    else {
        // Nothing above explains it: the thread is free and the page is not
        // unusually large, yet frames are still late. That points past
        // JavaScript, at the compositor or the GPU.
        verdict = 'JS は空いています — 描画側(GPU/合成)が疑わしい';
    }
    if (verdict) {
        const c = s.fps >= 45 ? OK : WARN;
        parts.push(`<div style="margin-top:8px;padding:5px 7px;border-radius:7px;`
            + `background:rgba(255,255,255,.05);color:${c}">${verdict}</div>`);
    }
    return parts.join('');
}
/**
 * The refresh interval for the panel, in ms.
 *
 * One second, not the 500 ms the media panel uses: on the machines this exists
 * for, the refresh is itself a measurable share of the frame budget.
 */
export const REFRESH_MS = 1000;
/**
 * Returns the panel's inner HTML for the current window.
 *
 * @returns {string}
 */
export function buildRenderStatsHtml() {
    return _render();
}
/**
 * The same figures as one line of plain text.
 *
 * A fallback readout for devices where the panel itself cannot be seen. The
 * panel is an overlay of our own making and can be defeated by a stacking
 * context, a fullscreen element or a theme rule; a notification is rendered by
 * the app's own notification system, so it appears wherever the rest of the UI
 * does. On a device we cannot inspect, that difference is the whole point.
 *
 * @returns {string}
 */
export function buildRenderStatsText() {
    const s = getRenderStats();
    const lines = [
        `FPS ${s.fps.toFixed(1)}　1フレーム 平均${s.frameAvgMs.toFixed(0)}/最大${s.frameMaxMs.toFixed(0)}ms`
            + `　カクつき${s.jankFrames}回`,
        s.longTaskSupported
            ? `長いタスク ${s.longTasks}回 占有${(s.longTaskMs / 50).toFixed(0)}% 最長${s.longTaskMaxMs.toFixed(0)}ms`
            : '長いタスク 非対応',
        s.eventTimingSupported && s.inputCount
            ? `操作の遅延 平均${s.inputAvgMs.toFixed(0)}/最大${s.inputMaxMs.toFixed(0)}ms`
            : '操作の遅延 —',
        `DOM ${s.domNodes}個　映像${s.videos}個`
            + (s.heapMB === null ? '' : `　JSメモリ${s.heapMB.toFixed(0)}MB`)
    ];
    return lines.join('\n');
}
